import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  mode: 'signin' | 'signup'
}

const AuthModal = ({ isOpen, onClose, mode: initialMode }: AuthModalProps) => {
  const { signInWithGoogle, signInWithApple, signInWithEmail, signUpWithEmail, resetPassword, loading } = useAuth()
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>(initialMode)
  const [showResetPassword, setShowResetPassword] = useState(false)

  // Reset form state when modal opens or mode changes
  useEffect(() => {
    if (isOpen) {
      setAuthMode(initialMode)
      setError('')
      setEmail('')
      setPassword('')
      setShowResetPassword(false)
    }
  }, [isOpen, initialMode])
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')

  const signupsOpen = import.meta.env.VITE_SIGNUPS_OPEN === 'true'

  if (!isOpen) return null

  const handleAppleAuth = async () => {
    try {
      setError('')
      await signInWithApple()
      onClose()
    } catch (_error) {
      // Keep modal open on failure
    }
  }

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!email || !email.includes('@')) {
      setError('Please enter your email address first.')
      return
    }
    try {
      await resetPassword(email)
      setShowResetPassword(false)
    } catch (_error) {
      // Error toasts handled in useAuth
    }
  }

  const handleGoogleAuth = async () => {
    try {
      setError('')
      await signInWithGoogle()
      onClose()
    } catch (_error) {
      // Keep modal open on failure — don't call onClose()
    }
  }

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.')
      return
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }

    try {
      if (authMode === 'signup') {
        await signUpWithEmail(email, password)
      } else {
        await signInWithEmail(email, password)
      }
      onClose()
    } catch (_error) {
      // Error toasts are handled in useAuth — keep modal open
    }
  }

  const toggleMode = () => {
    setAuthMode(authMode === 'signin' ? 'signup' : 'signin')
    setError('')
    setEmail('')
    setPassword('')
  }

  return (
    <div className="auth-modal-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="auth-modal-title">
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <div className="auth-modal-header">
          <h2 id="auth-modal-title" className="auth-modal-title">
            {authMode === 'signin' ? 'Welcome Back' : 'Join JustFixIt'}
          </h2>
          <button className="auth-modal-close" onClick={onClose} aria-label="Close">
            <X size={24} />
          </button>
        </div>

        <div className="auth-modal-content">
          {!signupsOpen && authMode === 'signup' ? (
            <>
              <div className="auth-closed-message">
                <p className="auth-closed-emoji">🚧</p>
                <p className="auth-closed-title">We're not quite ready yet!</p>
                <p className="auth-closed-text">
                  We're putting the finishing touches on justfixit.AI.
                  Join our interest list on the homepage to be first in line when we launch.
                </p>
              </div>
              <div className="auth-mode-toggle">
                <p className="auth-toggle-text">
                  Already have an account?{' '}
                  <button type="button" className="auth-toggle-link" onClick={toggleMode}>
                    Sign In
                  </button>
                </p>
              </div>
            </>
          ) : (
            <>
              <p className="auth-modal-description">
                {authMode === 'signin'
                  ? 'Sign in to access your account and continue fixing your computer issues.'
                  : 'Create your account to get started with AI-powered computer repair.'
                }
              </p>

              {showResetPassword ? (
                <form className="auth-email-form" onSubmit={handlePasswordReset}>
                  <p className="auth-reset-description">
                    Enter your email and we'll send you a link to reset your password.
                  </p>
                  <input
                    type="email"
                    className="auth-input"
                    placeholder="Email address"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError('') }}
                    disabled={loading}
                    autoComplete="email"
                  />
                  {error && <p className="auth-error">{error}</p>}
                  <button
                    type="submit"
                    className="auth-submit-button"
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </button>
                  <button
                    type="button"
                    className="auth-toggle-link auth-back-link"
                    onClick={() => setShowResetPassword(false)}
                  >
                    Back to sign in
                  </button>
                </form>
              ) : (
              <form className="auth-email-form" onSubmit={handleEmailAuth}>
                <input
                  type="email"
                  className="auth-input"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError('') }}
                  disabled={loading}
                  autoComplete="email"
                />
                <input
                  type="password"
                  className="auth-input"
                  placeholder="Password (min. 6 characters)"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError('') }}
                  disabled={loading}
                  autoComplete={authMode === 'signup' ? 'new-password' : 'current-password'}
                />
                {error && <p className="auth-error">{error}</p>}
                {authMode === 'signin' && (
                  <button
                    type="button"
                    className="auth-forgot-password"
                    onClick={() => setShowResetPassword(true)}
                  >
                    Forgot password?
                  </button>
                )}
                <button
                  type="submit"
                  className="auth-submit-button"
                  disabled={loading}
                >
                  {loading ? 'Please wait...' : (authMode === 'signup' ? 'Sign Up' : 'Sign In')}
                </button>
              </form>
              )}

              <div className="auth-divider">
                <span className="auth-divider-text">or</span>
              </div>

              <button
                className="google-auth-button"
                onClick={handleGoogleAuth}
                disabled={loading}
              >
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Continue with Google</span>
              </button>

              <button
                className="apple-auth-button"
                onClick={handleAppleAuth}
                disabled={loading}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
                <span>Continue with Apple</span>
              </button>

              <div className="auth-mode-toggle">
                <p className="auth-toggle-text">
                  {authMode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
                  <button type="button" className="auth-toggle-link" onClick={toggleMode}>
                    {authMode === 'signin' ? 'Sign Up' : 'Sign In'}
                  </button>
                </p>
              </div>

              <div className="auth-modal-footer">
                <p className="auth-modal-terms">
                  By continuing, you agree to our Terms of Service and Privacy Policy.
                  {authMode === 'signup' && ' You\'ll also be added to our mailing list for updates.'}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        .auth-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }

        .auth-modal {
          background: white;
          border-radius: 1.5rem;
          max-width: 400px;
          width: 100%;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .auth-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem 1.5rem 0;
        }

        .auth-modal-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1a1a2e;
        }

        .auth-modal-close {
          padding: 0.5rem;
          border-radius: 0.5rem;
          color: #6b7280;
          transition: all 0.2s;
        }

        .auth-modal-close:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .auth-modal-content {
          padding: 1.5rem;
        }

        .auth-modal-description {
          color: #6b7280;
          line-height: 1.6;
          margin-bottom: 2rem;
        }

        .auth-email-form {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          margin-bottom: 1.25rem;
        }

        .auth-input {
          width: 100%;
          padding: 0.875rem 1rem;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          font-size: 1rem;
          color: #1a1a2e;
          background: #ffffff;
          transition: all 0.2s;
          outline: none;
          box-sizing: border-box;
        }

        .auth-input:focus {
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }

        .auth-input:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .auth-error {
          color: #ef4444;
          font-size: 0.875rem;
          margin: 0;
        }

        .auth-submit-button {
          width: 100%;
          padding: 0.875rem 1rem;
          background: #6366f1;
          color: white;
          border-radius: 0.75rem;
          font-weight: 600;
          font-size: 1rem;
          transition: all 0.2s;
        }

        .auth-submit-button:hover {
          background: #5b21b6;
        }

        .auth-submit-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .auth-divider {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.25rem;
        }

        .auth-divider::before,
        .auth-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: #e5e7eb;
        }

        .auth-divider-text {
          color: #9ca3af;
          font-size: 0.875rem;
        }

        .auth-mode-toggle {
          text-align: center;
          margin-top: 1rem;
          margin-bottom: 1rem;
        }

        .auth-toggle-text {
          color: #6b7280;
          font-size: 0.875rem;
          margin: 0;
        }

        .auth-toggle-link {
          color: #6366f1;
          font-weight: 600;
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          font-size: 0.875rem;
        }

        .auth-toggle-link:hover {
          color: #5b21b6;
          text-decoration: underline;
        }

        .auth-closed-message {
          text-align: center;
          padding: 1.5rem 0;
        }

        .auth-closed-emoji {
          font-size: 3rem;
          margin-bottom: 1rem;
        }

        .auth-closed-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1a1a2e;
          margin-bottom: 0.75rem;
        }

        .auth-closed-text {
          color: #6b7280;
          line-height: 1.6;
          font-size: 0.95rem;
        }

        .google-auth-button,
        .apple-auth-button {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 0.875rem 1rem;
          border: 2px solid #e5e7eb;
          border-radius: 0.75rem;
          background: white;
          color: #374151;
          font-weight: 600;
          transition: all 0.2s;
          margin-bottom: 0.75rem;
        }

        .apple-auth-button {
          background: #000;
          color: #fff;
          border-color: #000;
          margin-bottom: 1.5rem;
        }

        .google-auth-button:hover {
          border-color: #d1d5db;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .apple-auth-button:hover {
          background: #1a1a1a;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        }

        .google-auth-button:disabled,
        .apple-auth-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .auth-forgot-password {
          background: none;
          border: none;
          color: #6366f1;
          font-size: 0.8125rem;
          cursor: pointer;
          padding: 0;
          text-align: right;
          margin-top: -0.25rem;
        }

        .auth-forgot-password:hover {
          color: #5b21b6;
          text-decoration: underline;
        }

        .auth-reset-description {
          color: #6b7280;
          font-size: 0.875rem;
          line-height: 1.5;
          margin-bottom: 0.5rem;
        }

        .auth-back-link {
          display: block;
          margin: 0.5rem auto 0;
          text-align: center;
        }

        .auth-modal-footer {
          border-top: 1px solid #f3f4f6;
          padding-top: 1rem;
        }

        .auth-modal-terms {
          font-size: 0.75rem;
          color: #9ca3af;
          line-height: 1.5;
          text-align: center;
        }
      `}</style>
    </div>
  )
}

export default AuthModal